import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../constants/common'
import { parseServerValidation } from '../../../utils/validation'
import { ConfirmationModal } from '../../Confirmation'
import { FormikForm } from '../../Formik'
import { useEmailCreationFormFields, useEmailDraft } from '../Email.hooks'
import { EmailFormContext } from '../EmailForm.context'

import {
  EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES,
  EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_RECIPIENTS_PRIVATE_NO_SPAM_MAX_COUNT,
} from './EmailCreationForm.constants'
import {
  checkEmailFormIsPrivateAccount,
  getEmailFormDocTemplates,
  getEmailFormPayload,
  getEmailFormSendErrors,
  getEmailFormValidationSchema,
  getPreparedEmailFormValues,
  parseEmailCreationFormServerError,
} from './EmailCreationForm.utils'
import { FormFields } from './FormFields'

import * as styles from './EmailCreationForm.module.css'

function EmailCreationForm({
  FormContainer,
  FromFieldComponent,
  aiLocalContext,
  containerProps = EMPTY_OBJECT,
  contentComponentProps,
  emailBlackList,
  excludeAutoAddAdditionalContacts = false,
  fieldsProps: initialFieldsProps,
  fileUploadLimitProps,
  formFieldsProps,
  formProps: {
    className: formBodyClassName = styles.formBody,
    ...formProps
  } = EMPTY_OBJECT,
  getValidationSchema = getEmailFormValidationSchema,
  initialNotifications,
  initialValues: initialFormValues = EMPTY_OBJECT,
  singleTemplateFolder,
  templateEntityApiParams,
  onCancel,
  onClose,
  onSubmit,
  onSuccess,
}) {
  const { t } = useTranslation()

  const formContext = useContext(EmailFormContext)

  const { blackList, setBlackList } = formContext

  const { setDraft: setEmailDraft } = useEmailDraft()

  const [fieldsProps, initialFieldsValues] =
    useEmailCreationFormFields(initialFieldsProps)

  const initialValues = {
    ...initialFormValues,
    ...initialFieldsValues,
    [EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES]:
      getEmailFormDocTemplates(
        initialFormValues[EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES]
      ),
  }

  const validationSchema = getValidationSchema()

  async function sendMail(values, { setErrors, setSubmitting }) {
    setErrors(EMPTY_OBJECT)

    let preparedValues = values

    try {
      preparedValues = await getPreparedEmailFormValues(values)
    } catch (error) {
      setSubmitting(false)
      setErrors(
        parseServerValidation(
          parseEmailCreationFormServerError(error?.response?.data || error, t)
        )
      )

      return
    }

    const payload = getEmailFormPayload(preparedValues)

    return onSubmit?.(payload, preparedValues)
      .catch(error => {
        formContext.onCloseModal()
        setSubmitting(false)
        setErrors(parseServerValidation(getEmailFormSendErrors({ error, t })))

        return null
      })
      .then(response => {
        if (response) {
          onSuccess?.({
            data: response.data,
            mass: values[EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING],
          })

          setEmailDraft(null, 0)
        }
      })
  }

  function handleSubmit(values, { setErrors, setSubmitting }) {
    const recipientsCount = values[EMAIL_CREATION_FORM_FIELD_NAME_TO].length

    if (
      checkEmailFormIsPrivateAccount(values) &&
      recipientsCount > EMAIL_RECIPIENTS_PRIVATE_NO_SPAM_MAX_COUNT
    ) {
      setSubmitting(false)

      formContext.setModal(
        <ConfirmationModal
          actionButtonsProps={{
            cancelText: t('Email.CancelBtnKey'),
            submitText: t('Email.MassMailingSendBtnKey', {
              count: recipientsCount,
            }),
          }}
          description={t('Modal.AttentionPrivateAccountMessageKey', {
            count: recipientsCount,
          })}
          title={t('Common.AttentionModalTitleKey')}
          onCancel={formContext.onCloseModal}
          onSubmit={() => sendMail(values, { setErrors, setSubmitting })}
        />
      )
    } else {
      sendMail(values, { setErrors, setSubmitting })
    }
  }

  return (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={containerProps}
      formProps={{
        ...formProps,
        className: formBodyClassName,
      }}
      initialValues={initialValues}
      innerRef={formContext.setInnerRef}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormFields
        {...formFieldsProps}
        FromFieldComponent={FromFieldComponent}
        aiLocalContext={aiLocalContext}
        blackList={emailBlackList || blackList}
        contentComponentProps={contentComponentProps}
        excludeAutoAddAdditionalContacts={excludeAutoAddAdditionalContacts}
        fieldsProps={fieldsProps}
        fileUploadLimitProps={fileUploadLimitProps}
        initialNotifications={initialNotifications}
        setBlackList={setBlackList}
        singleTemplateFolder={singleTemplateFolder}
        templateEntityApiParams={templateEntityApiParams}
        onCancel={onCancel}
        onClose={onClose}
      />
    </FormikForm>
  )
}

export default EmailCreationForm
